<script setup>
import { ref, computed, watch, onBeforeMount, defineProps, defineEmits } from "vue";
import Calendar from "primevue/calendar";
import ProgressBar from "primevue/progressbar";
import Tag from "primevue/tag";
import Checkbox from "primevue/checkbox";
import Dialog from "primevue/dialog";
import { useTrabajoStore } from "@/store/useTrabajoStore";
import { useTerritoriosEstadisticas } from "@/composables/useTerritoriosEstadisticas";
import { useTerritoriosStore } from "@/store/territorios";
import { useTerritorioStore } from "@/store/territorio";
import { useSalidaStore } from "@/store/useSalidaStore";
import { useScreenStore } from "@/store/useScreenStore";
import { useGruposInvitados } from "@/composables/useGruposInvitados";
import TarjetaTerritorio from "@/page/TarjetaTerritorio.vue";

const props = defineProps({
  start: {
    type: Date,
    default: null,
  },
  onlyIncomplete: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["selected"]);

const trabajo = useTrabajoStore();
const territorios = useTerritoriosStore();
const territorio = useTerritorioStore();
const salida = useSalidaStore();
const screen = useScreenStore();
const { getGruposInvitados } = useGruposInvitados();

const gruposInvitadosList = ref([]);

onBeforeMount(async () => {
  await trabajo.syncHistory();
  try {
    const result = await getGruposInvitados();
    gruposInvitadosList.value = result.guestGroups || [];
  } catch (e) {
    console.error("Error cargando grupos de invitados", e);
  }
});

const { build } = useTerritoriosEstadisticas();
const start = ref(props.start || new Date(new Date().setMonth(new Date().getMonth() - 1)));
const filter = ref("");
const expandedRows = ref({});
const visibleWork = ref(false);

watch(
  () => props.start,
  (val) => {
    if (val) start.value = val;
  }
);

const items = computed(() =>
  start.value ? build(start.value, { onlyIncomplete: props.onlyIncomplete }) : []
);
const filteredItems = computed(() => {
  if (!filter.value) return items.value;
  const f = filter.value.toLowerCase();
  return items.value.filter((i) => i.name.toLowerCase().includes(f));
});

const progresoPct = (item) => {
  if (!item.manzanasTotal) return 100;
  return Math.round(
    ((item.manzanasTotal - item.manzanasPendientesTotal) / item.manzanasTotal) * 100
  );
};

// --- Informado en las últimas 48hs ---

const HORAS_48 = 48 * 60 * 60 * 1000;

// Último grupo (de FormSalidas) que tuvo asignado el territorio, para poder
// mostrar quién lo trabajó: grupo(s) de predicación + conductor + invitados.
const ultimaSalidaDeTerritorio = (name) => {
  const todas = [...(salida.list.data || []), ...(salida.history || [])];
  return todas
    .filter((s) => Array.isArray(s.territorios) && s.territorios.includes(name))
    .sort((a, b) => (Number(b.date) || 0) - (Number(a.date) || 0))[0];
};

const grupoLabel = (val) => territorios.grupos.find((g) => g.id === val)?.name || val;

const conductorName = (val) => {
  if (!val) return "";
  return salida.conductores.find((c) => c.id === val)?.name || val;
};

const invitadosDeGrupos = (grupos) => {
  if (!grupos?.length) return [];
  return gruposInvitadosList.value.filter((gi) =>
    (gi.grupos || []).some((gid) => grupos.includes(gid))
  );
};

// Congregaciones asociadas a los grupos de la salida (cada zona representa una
// congregación), igual que congregacionesDeSalida en FormSalidas.vue.
const congregacionesDeGrupos = (grupos) => {
  const set = new Set();
  (grupos || []).forEach((gid) => {
    const zonaId = territorios.grupos.find((g) => g.id === gid)?.zonaId;
    const zona = zonaId ? territorios.zonas.find((z) => z.id === zonaId) : null;
    if (zona?.name) set.add(zona.name);
  });
  return [...set];
};

const asignacionDeTerritorio = (name) => {
  const ultima = ultimaSalidaDeTerritorio(name);
  if (!ultima) return null;
  const congregaciones = congregacionesDeGrupos(ultima.grupos);
  return {
    grupoIds: ultima.grupos || [],
    grupos: (ultima.grupos || []).map(grupoLabel).join(", "),
    congregaciones,
    congregacionesLabel: congregaciones.join(", "),
    conductor: conductorName(ultima.conductor),
    invitados: invitadosDeGrupos(ultima.grupos)
      .map((gi) => gi.codigo)
      .join(", "),
  };
};

const informadosUltimas48h = computed(() => {
  const cutoff = Date.now() - HORAS_48;
  const rows = [];

  for (const t of territorios.listWithName) {
    const uuid = t.uuid;
    if (!uuid) continue;
    const workData = trabajo.data[uuid];
    if (!workData?.ultimaActividad || workData.ultimaActividad < cutoff) continue;

    const manzanasHechas = workData.manzanasTotal - workData.manzanasPendientesTotal;
    if (manzanasHechas === 0) continue;

    rows.push({
      uuid,
      name: t.territorio_nombre,
      manzanasHechas,
      manzanasTotal: workData.manzanasTotal,
      manzanasPendientesTotal: workData.manzanasPendientesTotal,
      ultimaActividad: workData.ultimaActividad,
      asignacion: asignacionDeTerritorio(t.territorio_nombre),
    });
  }

  return rows.sort((a, b) => b.ultimaActividad - a.ultimaActividad);
});

// Filtros de Grupo / Congregación para la sección de últimas 48hs
const filtroGrupo48h = ref(null);
const filtroCongregacion48h = ref(null);

const congregacionOptions48h = computed(() => {
  const set = new Set();
  informadosUltimas48h.value.forEach((row) => {
    (row.asignacion?.congregaciones || []).forEach((c) => set.add(c));
  });
  return [...set].sort((a, b) => a.localeCompare(b));
});

const informadosUltimas48hFiltrado = computed(() => {
  let result = informadosUltimas48h.value;

  if (filtroGrupo48h.value) {
    result = result.filter((row) => row.asignacion?.grupoIds.includes(filtroGrupo48h.value));
  }

  if (filtroCongregacion48h.value) {
    result = result.filter((row) =>
      row.asignacion?.congregaciones.includes(filtroCongregacion48h.value)
    );
  }

  return result;
});

const isSelected = (item) => props.selected.includes(item.name);

const rowClass = (data) => (isSelected(data) ? "fila-seleccionada" : "");

const onRowClick = ({ data }) => {
  if (props.selectable) {
    emit("selected", data.name);
    return;
  }
  const doc = territorios.getTerritorioById(data.uuid);
  if (!doc) return;
  territorio.cargar(doc);
  visibleWork.value = true;
};
</script>

<template>
  <div class="p-3">
    <h1 class="text-center" v-if="!props.selectable">Informes de territorio</h1>

    <div class="mb-4" v-if="!props.selectable">
      <h3 class="mb-2">Informado en las últimas 48 horas</h3>
      <div class="flex gap-3 flex-column md:flex-row mb-3">
        <div class="flex flex-column gap-2">
          <label>Filtrar por grupo:</label>
          <Dropdown
            v-model="filtroGrupo48h"
            :options="territorios.grupos"
            optionLabel="name"
            optionValue="id"
            placeholder="Todos los grupos"
            showClear
            class="w-full md:w-15rem"
          />
        </div>
        <div class="flex flex-column gap-2">
          <label>Filtrar por congregación:</label>
          <Dropdown
            v-model="filtroCongregacion48h"
            :options="congregacionOptions48h"
            placeholder="Todas las congregaciones"
            showClear
            class="w-full md:w-15rem"
          />
        </div>
      </div>
      <DataTable
        :value="informadosUltimas48hFiltrado"
        class="p-datatable-sm"
        dataKey="uuid"
        sortField="ultimaActividad"
        :sortOrder="-1"
      >
        <Column field="name" header="Territorio" sortable />
        <Column header="Grupo">
          <template #body="{ data }">
            <div v-if="data.asignacion" class="flex flex-column">
              <span v-if="data.asignacion.grupos">{{ data.asignacion.grupos }}</span>
              <span v-else class="text-color-secondary">—</span>
              <span v-if="data.asignacion.conductor" class="text-xs text-color-secondary">
                Conductor: {{ data.asignacion.conductor }}
              </span>
              <span v-if="data.asignacion.invitados" class="text-xs text-color-secondary">
                Invitados: {{ data.asignacion.invitados }}
              </span>
            </div>
            <span v-else class="text-color-secondary">—</span>
          </template>
        </Column>
        <Column header="Congregación">
          <template #body="{ data }">
            {{ data.asignacion?.congregacionesLabel || "—" }}
          </template>
        </Column>
        <Column header="Manzanas hechas">
          <template #body="{ data }">{{ data.manzanasHechas }}</template>
        </Column>
        <Column header="Manzanas restantes">
          <template #body="{ data }">{{ data.manzanasPendientesTotal }}</template>
        </Column>
        <Column field="ultimaActividad" header="Informado el" sortable>
          <template #body="{ data }">{{ trabajo.getDateTime(data.ultimaActividad) }}</template>
        </Column>
        <template #empty>Sin informes en las últimas 48 horas.</template>
      </DataTable>
    </div>

    <div class="flex gap-3 flex-column md:flex-row mb-3">
      <div class="flex flex-column gap-2">
        <label>Fecha de inicio:</label>
        <Calendar v-model="start" dateFormat="dd/mm/yy" />
      </div>
      <div class="flex flex-column gap-2 flex-1">
        <label>Filtrar por territorio:</label>
        <IconField iconPosition="left">
          <InputIcon class="pi pi-filter" />
          <InputText v-model="filter" placeholder="Nombre de territorio" />
        </IconField>
      </div>
    </div>

    <DataTable
      :value="filteredItems"
      v-model:expandedRows="expandedRows"
      dataKey="uuid"
      class="p-datatable-sm"
      :rowClass="rowClass"
      @row-click="onRowClick"
    >
      <Column expander style="width: 3rem" />
      <Column v-if="props.selectable" style="width: 3rem">
        <template #body="{ data }">
          <Checkbox :binary="true" :modelValue="isSelected(data)" @click.stop="onRowClick({ data })" />
        </template>
      </Column>
      <Column field="name" header="Territorio" sortable />
      <Column header="Referencia">
        <template #body="{ data }">{{ data.referencia }}</template>
      </Column>
      <Column header="Estado">
        <template #body="{ data }">
          <Tag
            :value="data.asignado ? 'Asignado' : 'Disponible'"
            :severity="data.asignado ? 'danger' : 'success'"
          />
        </template>
      </Column>
      <Column header="Progreso">
        <template #body="{ data }">
          <div class="flex align-items-center gap-2">
            <ProgressBar
              :value="progresoPct(data)"
              :showValue="false"
              style="height: 8px; width: 100px"
            />
            <span class="text-sm"
              >{{ data.manzanasTotal - data.manzanasPendientesTotal }}/{{
                data.manzanasTotal
              }}</span
            >
          </div>
        </template>
      </Column>
      <Column header="Última completación">
        <template #body="{ data }">{{
          trabajo.getDate(data.ultimaCompletacion, "—")
        }}</template>
      </Column>
      <Column header="Veces completado" field="vecesCompletado" sortable>
        <template #body="{ data }">{{ data.vecesCompletado }}</template>
      </Column>
      <template #expansion="{ data }">
        <div class="p-3">
          <h5>Historial de asignaciones</h5>
          <table class="ciclos-table" v-if="data.ciclos.length">
            <thead>
              <tr>
                <th>Conductor</th>
                <th>Inicio</th>
                <th>Completado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in data.ciclos" :key="i">
                <td>{{ c.conductor }}</td>
                <td>{{ trabajo.getDate(c.inicio) }}</td>
                <td>{{ c.fin ? trabajo.getDate(c.fin) : "En curso" }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="text-color-secondary">
            Sin registros en el período seleccionado.
          </div>
        </div>
      </template>
      <template #empty>No hay territorios para mostrar.</template>
    </DataTable>
  </div>
  <Dialog
    v-model:visible="visibleWork"
    modal
    header="Registro de trabajo"
    style="width: 95vw"
  >
    <TarjetaTerritorio :isMobile="screen.isMobile" />
  </Dialog>
</template>

<style scoped>
:deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}
:deep(.fila-seleccionada) {
  background: var(--accent-a10, rgba(59, 130, 246, 0.1));
}
.ciclos-table {
  width: 100%;
  border-collapse: collapse;
}
.ciclos-table th,
.ciclos-table td {
  text-align: left;
  padding: 4px 8px;
  border-bottom: 1px solid var(--surface-border, #ddd);
}
</style>
